import React from 'react';
import { Calendar, Calculator, Plus, Minus } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatDate } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрых расчетов дат
 */
const QuickToolsSidebar = ({ 
  formData, 
  setFormData, 
  errors, 
  isCalculating 
}) => {
  
  // Обработчик быстрого добавления периода
  const handleQuickAddPeriod = (amount, unit) => {
    const today = new Date();
    let endDate;

    switch (unit) {
      case 'day':
        endDate = new Date(today);
        endDate.setDate(today.getDate() + amount);
        break;
      case 'week':
        endDate = new Date(today);
        endDate.setDate(today.getDate() + (amount * 7));
        break;
      case 'month':
        endDate = new Date(today);
        endDate.setMonth(today.getMonth() + amount);
        break;
      case 'quarter':
        endDate = new Date(today);
        endDate.setMonth(today.getMonth() + (amount * 3));
        break;
      case 'year':
        endDate = new Date(today);
        endDate.setFullYear(today.getFullYear() + amount);
        break;
      default:
        endDate = new Date(today);
        endDate.setDate(today.getDate() + amount);
    }

    setFormData(prev => ({
      ...prev,
      calculationType: 'difference',
      startDate: today.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    }));
  };
  
  // Обработчик быстрого расчета от определенной даты
  const handleQuickFromDate = (direction, days) => {
    setFormData(prev => ({
      ...prev,
      calculationType: direction === 'add' ? 'add' : 'subtract',
      daysToAddSubtract: String(days)
    }));
  };

  // Обработчик переключения типа расчета
  const handleSetCalculationType = (type) => {
    setFormData(prev => ({
      ...prev,
      calculationType: type
    }));
  };

  // Предопределенные периоды для быстрого выбора
  const quickPeriods = [
    { label: '7 дней', unit: 'day', amount: 7 },
    { label: '30 дней', unit: 'day', amount: 30 },
    { label: '90 дней', unit: 'day', amount: 90 },
    { label: '1 год', unit: 'year', amount: 1 }
  ];

  // Переключатели типов расчета
  const calculationTypes = [
    { id: 'difference', name: 'Разница между датами', icon: <Calendar className="w-4 h-4" /> },
    { id: 'add', name: 'Добавить дни', icon: <Plus className="w-4 h-4" /> },
    { id: 'subtract', name: 'Вычесть дни', icon: <Minus className="w-4 h-4" /> }
  ];
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-cyan-500" />
        Быстрые расчеты
      </h3>
      
      <div className="space-y-5">
        {/* Переключатель типа расчета */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Выберите тип расчета:
          </div>
          <div className="space-y-2">
            {calculationTypes.map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => handleSetCalculationType(type.id)}
                className={`flex items-center w-full p-2.5 rounded-lg border transition-colors
                  ${formData.calculationType === type.id 
                    ? 'bg-cyan-50 dark:bg-cyan-900/30 border-cyan-200 dark:border-cyan-800 text-cyan-800 dark:text-cyan-200' 
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
              >
                <span className={`mr-2 ${formData.calculationType === type.id ? 'text-cyan-600 dark:text-cyan-400' : 'text-gray-500 dark:text-gray-400'}`}>
                  {type.icon}
                </span>
                <span className="text-sm font-medium">{type.name}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Быстрые периоды */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Быстрые периоды от сегодня:
          </div>
          <div className="grid grid-cols-2 gap-2">
            {quickPeriods.map((period, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleQuickAddPeriod(period.amount, period.unit)}
                className="p-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Быстрые добавления/вычитания */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Быстрые действия с днями:
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleQuickFromDate('add', 7)}
              className="p-2 text-sm rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 text-green-700 dark:text-green-300 transition-colors flex items-center justify-center"
            >
              <Plus className="w-3 h-3 mr-1" /> 7 дней
            </button>
            <button
              type="button"
              onClick={() => handleQuickFromDate('add', 30)}
              className="p-2 text-sm rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 text-green-700 dark:text-green-300 transition-colors flex items-center justify-center"
            >
              <Plus className="w-3 h-3 mr-1" /> 30 дней
            </button>
            <button
              type="button"
              onClick={() => handleQuickFromDate('subtract', 7)}
              className="p-2 text-sm rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-700 dark:text-red-300 transition-colors flex items-center justify-center"
            >
              <Minus className="w-3 h-3 mr-1" /> 7 дней
            </button>
            <button
              type="button"
              onClick={() => handleQuickFromDate('subtract', 30)}
              className="p-2 text-sm rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-700 dark:text-red-300 transition-colors flex items-center justify-center"
            >
              <Minus className="w-3 h-3 mr-1" /> 30 дней
            </button>
          </div>
        </div>
        
        {/* Популярные расчеты */}
        <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Популярные расчеты:
          </div>
          <div className="space-y-2">
            <Button
              fullWidth
              variant="outline"
              onClick={() => {
                const today = new Date();
                const endOfYear = new Date(today.getFullYear(), 11, 31);
                setFormData(prev => ({
                  ...prev,
                  calculationType: 'difference',
                  startDate: today.toISOString().split('T')[0],
                  endDate: endOfYear.toISOString().split('T')[0],
                  includeEndDate: true
                }));
              }}
              size="sm"
            >
              Дней до конца года
            </Button>
            <Button
              fullWidth
              variant="outline"
              onClick={() => {
                const today = new Date();
                // Новый год следующего года
                const newYear = new Date(today.getFullYear() + 1, 0, 1);
                setFormData(prev => ({
                  ...prev,
                  calculationType: 'difference',
                  startDate: today.toISOString().split('T')[0],
                  endDate: newYear.toISOString().split('T')[0],
                  includeEndDate: true
                }));
              }}
              size="sm"
            >
              Дней до Нового года
            </Button>
            <Button
              fullWidth
              variant="outline"
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  calculationType: 'difference',
                  countWorkingDaysOnly: true,
                  excludeHolidays: true,
                  useRussianHolidays: true
                }));
              }}
              size="sm"
            >
              Только рабочие дни
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickToolsSidebar;